import { ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { cx, neutralTile } from '../../lib/utils'

/**
 * Which kind of work a row is about.
 *
 * The same three hues as the timeline, and for the same reason: an operator
 * moving between the Surveys, Reports and Tasks tabs should find the same
 * colour meaning the same thing. The names say the kind rather than the
 * colour, so the palette can move without the meaning moving with it.
 */
export type RowKind = 'survey' | 'report' | 'task'

const KIND_CLASSES: Record<RowKind, string> = {
  survey: neutralTile,
  report: neutralTile,
  task: neutralTile,
}

const TITLE_CLASSES = 'block truncate font-semibold text-gray-950 dark:text-white'

interface PanelRowProps {
  /** Where the row leads. A row without one is a record, not a door. */
  to?: string
  kind: RowKind
  icon: ReactNode
  title: string
  meta: ReactNode
  /**
   * Badges, in reading order. The rightmost is the last thing the eye meets
   * before the edge, so put the state an operator is scanning for there and
   * the merely descriptive ones before it.
   */
  badges?: ReactNode
  /** A control belonging to this row, sitting after the badges. */
  action?: ReactNode
}

/**
 * One line in a workspace panel.
 *
 * The three panels each wrote this out by hand, which is how a list of
 * surveys ends up a different shape from a list of reports for no reason
 * anybody chose. Sharing it means the density, the hover and the focus ring
 * are decided once.
 *
 * The link covers the row rather than being the row, so a control can sit
 * inside one without nesting a button in an anchor — which is invalid markup
 * and unreachable by keyboard. The row then borrows the link's focus ring.
 */
export default function PanelRow({ to, kind, icon, title, meta, badges, action }: PanelRowProps) {
  return (
    <div
      className={cx(
        'relative flex flex-wrap items-center gap-x-4 gap-y-2 p-4 transition sm:flex-nowrap',
        to && 'hover:bg-gray-50 dark:hover:bg-gray-900',
        // See `focusRing` in lib/utils for why the style is an arbitrary
        // property. It matters more here than anywhere: this row is a plain
        // div, so there is no browser default ring to fall back on.
        'has-[a:focus-visible]:[outline-style:solid] has-[a:focus-visible]:outline-2',
        'has-[a:focus-visible]:-outline-offset-2 has-[a:focus-visible]:outline-primary-600 dark:has-[a:focus-visible]:outline-gray-50',
      )}
    >
      <span
        aria-hidden="true"
        className={cx(
          'flex size-9 shrink-0 items-center justify-center rounded-lg ring-1',
          KIND_CLASSES[kind],
        )}
      >
        {icon}
      </span>

      <div className="min-w-0 flex-[1_1_12rem]">
        {/* The title truncates on a span inside the link, never on the link
            itself: `truncate` hides overflow, and an overflow-hidden anchor
            clips away the very pseudo-element that covers the row. */}
        {to ? (
          <Link to={to} className="block outline-none after:absolute after:inset-0">
            <span className={TITLE_CLASSES}>{title}</span>
          </Link>
        ) : (
          <span className={TITLE_CLASSES}>{title}</span>
        )}
        <p className="mt-0.5 truncate text-sm text-gray-500 dark:text-gray-400">{meta}</p>
      </div>

      {(badges || action) && (
        <div className="relative ml-[3.25rem] flex flex-wrap items-center gap-2 sm:ml-0 sm:shrink-0">
          {badges}
          {action}
        </div>
      )}
    </div>
  )
}
